import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Users, TrendingUp, Route, Eye } from 'lucide-react';

const COLORS = ['hsl(0,84%,60%)', 'hsl(38,80%,55%)', 'hsl(160,60%,45%)', 'hsl(240,95%,34%)'];

interface AnalyticsRow {
  session_id: string | null;
  page_path: string;
  visit_duration_seconds: number | null;
  created_at: string;
}

interface SectionViewRow {
  session_id: string;
  section_id: string;
  page_path: string;
  time_spent_seconds: number;
}

interface Props {
  analytics: AnalyticsRow[];
  sectionViews: SectionViewRow[];
}

const UserBehaviorInsights = ({ analytics, sectionViews }: Props) => {
  // Engagement buckets
  const buckets = useMemo(() => {
    const sessions: Record<string, { duration: number; pages: number }> = {};
    analytics.forEach((r) => {
      const sid = r.session_id || 'unknown';
      if (!sessions[sid]) sessions[sid] = { duration: 0, pages: 0 };
      sessions[sid].pages += 1;
      const d = (r as any).visit_duration_seconds;
      if (d && d > sessions[sid].duration) sessions[sid].duration = d;
    });

    let bounced = 0, browser = 0, engaged = 0, power = 0;
    Object.values(sessions).forEach(({ duration, pages }) => {
      if (duration < 10 || pages <= 1) bounced++;
      else if (duration < 60) browser++;
      else if (duration < 300) engaged++;
      else power++;
    });

    return [
      { name: 'Bounced (<10s)', value: bounced },
      { name: 'Browser (10-60s)', value: browser },
      { name: 'Engaged (1-5m)', value: engaged },
      { name: 'Power User (5m+)', value: power },
    ].filter((b) => b.value > 0);
  }, [analytics]);

  const totalSessions = useMemo(() => new Set(analytics.map((a) => a.session_id)).size, [analytics]);
  const bounceRate = useMemo(() => {
    if (totalSessions === 0) return 0;
    const bouncedCount = buckets.find((b) => b.name.startsWith('Bounced'))?.value || 0;
    return Math.round((bouncedCount / totalSessions) * 100);
  }, [buckets, totalSessions]);

  // Section popularity
  const sectionPopularity = useMemo(() => {
    const map: Record<string, { views: number; totalTime: number }> = {};
    sectionViews.forEach((sv) => {
      if (!map[sv.section_id]) map[sv.section_id] = { views: 0, totalTime: 0 };
      map[sv.section_id].views += 1;
      map[sv.section_id].totalTime += sv.time_spent_seconds;
    });
    return Object.entries(map)
      .sort((a, b) => b[1].views - a[1].views)
      .slice(0, 12)
      .map(([name, { views, totalTime }]) => ({
        name: `#${name}`,
        views,
        avgTime: views > 0 ? Math.round(totalTime / views) : 0,
      }));
  }, [sectionViews]);

  // Page engagement
  const pageEngagement = useMemo(() => {
    const map: Record<string, { visits: number; totalDuration: number; durCount: number }> = {};
    analytics.forEach((r) => {
      if (!map[r.page_path]) map[r.page_path] = { visits: 0, totalDuration: 0, durCount: 0 };
      map[r.page_path].visits += 1;
      const d = (r as any).visit_duration_seconds;
      if (d && d > 0) {
        map[r.page_path].totalDuration += d;
        map[r.page_path].durCount += 1;
      }
    });
    return Object.entries(map)
      .sort((a, b) => b[1].visits - a[1].visits)
      .slice(0, 10)
      .map(([page, { visits, totalDuration, durCount }]) => ({
        page,
        visits,
        avgDuration: durCount > 0 ? Math.round(totalDuration / durCount) : 0,
      }));
  }, [analytics]);

  // Session flows (top 5 journeys)
  const sessionFlows = useMemo(() => {
    const sessions: Record<string, { pages: string[]; time: string }[]> = {};
    analytics.forEach((r) => {
      const sid = r.session_id || 'unknown';
      if (!sessions[sid]) sessions[sid] = [];
      sessions[sid].push({ pages: [r.page_path], time: r.created_at });
    });

    const flowMap: Record<string, number> = {};
    Object.values(sessions).forEach((entries) => {
      const sorted = entries.sort((a, b) => a.time.localeCompare(b.time));
      const path = sorted.map((e) => e.pages[0]).join(' → ');
      flowMap[path] = (flowMap[path] || 0) + 1;
    });

    return Object.entries(flowMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [analytics]);

  const formatDur = (s: number) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`);
  const maxSectionViews = sectionPopularity[0]?.views || 1;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold font-fraunces" style={{ color: 'var(--blue)' }}>User Behavior Insights</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Engagement Buckets */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2" style={{ color: 'var(--ink)' }}>
              <Users className="w-4 h-4" /> Engagement Buckets
            </CardTitle>
          </CardHeader>
          <CardContent>
            {buckets.length === 0 ? (
              <p className="text-muted-foreground text-sm">No session data</p>
            ) : (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width={120} height={120}>
                  <PieChart>
                    <Pie data={buckets} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={30} outerRadius={50}>
                      {buckets.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1">
                  {buckets.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-2 text-xs">
                      <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-muted-foreground">{d.name}: {d.value}</span>
                    </div>
                  ))}
                  <div className="mt-2 text-xs font-medium" style={{ color: 'var(--ink)' }}>
                    Bounce rate: {bounceRate}%
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section Popularity */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2" style={{ color: 'var(--ink)' }}>
              <Eye className="w-4 h-4" /> Section Popularity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sectionPopularity.length === 0 ? (
              <p className="text-muted-foreground text-sm">No section data yet — data will appear as visitors browse your site</p>
            ) : (
              <div className="space-y-2">
                {sectionPopularity.map((s) => (
                  <div key={s.name}>
                    <div className="flex items-center justify-between text-xs mb-0.5">
                      <span style={{ color: 'var(--ink)' }}>{s.name}</span>
                      <span className="text-muted-foreground">{s.views} views · avg {formatDur(s.avgTime)}</span>
                    </div>
                    <div className="h-1.5 rounded-full" style={{ background: 'var(--line-slate)' }}>
                      <div className="h-full rounded-full" style={{ width: `${(s.views / maxSectionViews) * 100}%`, background: 'hsl(160,60%,45%)' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Session Flows */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2" style={{ color: 'var(--ink)' }}>
              <Route className="w-4 h-4" /> Top Session Flows
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sessionFlows.length === 0 ? (
              <p className="text-muted-foreground text-sm">No flow data yet</p>
            ) : (
              <div className="space-y-2">
                {sessionFlows.map(([flow, count], i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="text-muted-foreground shrink-0 w-4 text-right font-mono">{count}×</span>
                    <span className="break-all" style={{ color: 'var(--ink)' }}>{flow}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Page Engagement Table */}
      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2" style={{ color: 'var(--ink)' }}>
            <TrendingUp className="w-4 h-4" /> Page Engagement
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pageEngagement.length === 0 ? (
            <p className="text-muted-foreground text-sm">No page data yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--line-slate)' }}>
                    <th className="text-left py-2 text-muted-foreground font-normal text-xs">Page</th>
                    <th className="text-right py-2 text-muted-foreground font-normal text-xs w-20">Visits</th>
                    <th className="text-right py-2 text-muted-foreground font-normal text-xs w-24">Avg Duration</th>
                    <th className="text-right py-2 text-muted-foreground font-normal text-xs w-20">Sticky?</th>
                  </tr>
                </thead>
                <tbody>
                  {pageEngagement.map((p) => (
                    <tr key={p.page} className="border-b" style={{ borderColor: 'var(--line-slate)' }}>
                      <td className="py-2 truncate max-w-[200px]" style={{ color: 'var(--ink)' }}>{p.page}</td>
                      <td className="py-2 text-right font-medium" style={{ color: 'var(--ink)' }}>{p.visits}</td>
                      <td className="py-2 text-right text-muted-foreground">{formatDur(p.avgDuration)}</td>
                      <td className="py-2 text-right">
                        {p.avgDuration >= 60 ? '🔥' : p.avgDuration >= 30 ? '👍' : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserBehaviorInsights;
